import React, { useState } from "react";
import { useDisclosure } from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

// Icon imports
import { AiOutlineLock } from "react-icons/ai";
import { BiLike, BiLogOutCircle } from "react-icons/bi";
import { FiUser } from "react-icons/fi";
import { MdKeyboardArrowRight, MdOutlineLocationOn } from "react-icons/md";
import { RiChatSmileLine, RiDeleteBinLine } from "react-icons/ri";
import { TbArrowsRightLeft } from "react-icons/tb";
import { BsVectorPen } from "react-icons/bs";

// Component imports
import DashboardLayout from "../DashboardHome/DashboardLayout";
import EditProfile from "./EditProfile";
import ChangePassword from "./ChangePassword";
import ChangeLocation from "./ChangeLocation";
import Support from "./SupportHelp";
import Switch from "./SwitchAccount";
import DeleteAccount from "./DeleteAccount";
import ThemeToggle from "../ThemeButton/Toggle";

// Redux actions
import { logOut } from "../../redux/auth/auth.actions";

// Account menu item component to reduce repetition
const AccountMenuItem = ({ icon, label, onClick, rightElement, iconColor }) => {
  const darktheme = useSelector((state) => state.features.settings.darktheme);
  const IconComponent = icon;
  
  return (
    <div className="flex justify-between cursor-pointer items-center mb-[28px]" onClick={onClick}>
      <div className="flex">
        <span>
          <IconComponent 
            size={"25px"} 
            className={iconColor || (darktheme ? "bg-[#09121F]" : "text-kdark3")} 
          />
        </span>
        <span className={`ml-[19px] text-kbody6 ${label === "Delete Account" ? "text-[#F97066]" : ""}`}>
          {label}
        </span>
      </div>
      <div>
        {rightElement || <MdKeyboardArrowRight />}
      </div>
    </div>
  );
};

function Account() {
  // Hooks
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [accountModalId, setAccountModalId] = useState(1);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Redux state
  const { user } = useSelector((state) => state.auth);

  // Open modal with specific content based on ID
  const handleAccountModal = (id) => {
    setAccountModalId(id);
    onOpen();
  };

  // Handle user logout
  const handleLogout = () => {
    dispatch(logOut());
    window.location.reload(false);
    navigate("/dashboard/home");
  };

  // Account section menu items configuration
  const accountMenuItems = [
    { id: 1, icon: FiUser, label: "Edit profile" },
    { id: 2, icon: MdOutlineLocationOn, label: "Change location" }
  ];

  // Activity section menu items configuration
  const activityMenuItems = [
    { 
      id: null, 
      icon: BsVectorPen, 
      label: "Theme", 
      rightElement: <ThemeToggle />,
      onClick: () => {} 
    },
    { id: 3, icon: AiOutlineLock, label: "Change password" }
  ];

  // Support section menu items configuration
  const supportMenuItems = [
    { id: 5, icon: RiChatSmileLine, label: "Help & Support" },
    { id: null, icon: BiLike, label: "Rate Arad in store" }
  ];

  // Manage section menu items configuration
  const manageMenuItems = [
    { id: 4, icon: TbArrowsRightLeft, label: "Switch account" },
    { id: null, icon: BiLogOutCircle, label: "Logout", onClick: handleLogout },
    { 
      id: 6, 
      icon: RiDeleteBinLine, 
      label: "Delete Account", 
      iconColor: "text-[#F97066]" 
    }
  ];

  return (
    <DashboardLayout>
      <div className="w-full">
        <div className="lg:w-[80%] mt-4 md:mt-[35px] lg:mr-[130px]">
          {/* Page header */}
          <div className="w-full border-b border-[#cac8c8] hidden lg:flex font-semibold text-kbody6 pb-[15px]">
            <div className="w-full flex items-center lg:pl-[41px]">
              <span className="ml-4 md:ml-5 text-lg font-medium lg:text-[28px]">Account</span>
            </div>
          </div>
          
          {/* Main content */}
          <div className="mt-10 lg:pl-[41px] lg:pt-[29px]">
            <div className="flex flex-col md:flex-row mx-4 lg:mx-0 justify-evenly">
              {/* Left column - Account & Activity */}
              <div className="flex-col gap-10 justify-evenly basis-[40%]">
                {/* Account section */}
                <div className="flex flex-col basis-[40%]">
                  <div className="mb-[23px] uppercase text-kbody7">Account</div>
                  <div>
                    {accountMenuItems.map((item) => (
                      <AccountMenuItem 
                        key={item.id}
                        icon={item.icon}
                        label={item.label}
                        onClick={() => handleAccountModal(item.id)}
                      />
                    ))}
                  </div>
                </div>
                
                {/* Activity section */}
                <div className="flex flex-col basis-[40%]">
                  <div className="mb-[23px] uppercase text-kbody7">Activity</div>
                  <div>
                    {activityMenuItems.map((item, index) => (
                      <AccountMenuItem 
                        key={index}
                        icon={item.icon}
                        label={item.label}
                        onClick={item.id ? () => handleAccountModal(item.id) : item.onClick}
                        rightElement={item.rightElement}
                      />
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Right column - Support & Manage */}
              <div className="flex-col gap-10 justify-evenly basis-[40%]">
                {/* Support section */}
                <div className="flex flex-col basis-[40%]">
                  <div className="mb-[23px] uppercase text-kbody7">Support</div>
                  <div>
                    {supportMenuItems.map((item, index) => (
                      <AccountMenuItem 
                        key={index}
                        icon={item.icon}
                        label={item.label}
                        onClick={item.id ? () => handleAccountModal(item.id) : () => {}}
                      />
                    ))}
                  </div>
                </div>
                
                {/* Manage section */}
                <div className="flex flex-col basis-[40%]">
                  <div className="mb-[23px] uppercase text-kbody7">Manage</div>
                  <div>
                    {manageMenuItems.map((item, index) => (
                      <AccountMenuItem 
                        key={index}
                        icon={item.icon}
                        label={item.label}
                        onClick={item.onClick || 
                          (item.id ? () => handleAccountModal(item.id) : () => {})}
                        iconColor={item.iconColor}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Modal components */}
      <EditProfile isOpen={accountModalId === 1 && isOpen} onClose={onClose} type={"Edit"} />
      <ChangeLocation isOpen={accountModalId === 2 && isOpen} onClose={onClose} location={user?.coords} />
      <ChangePassword isOpen={accountModalId === 3 && isOpen} onClose={onClose} />
      <Switch isOpen={accountModalId === 4 && isOpen} onClose={onClose} />
      <Support isOpen={accountModalId === 5 && isOpen} onClose={onClose} />
      <DeleteAccount isOpen={accountModalId === 6 && isOpen} onClose={onClose} />
    </DashboardLayout>
  );
}

export default Account;